import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal",
    "sessionsList",
    "selectedDate",
    "dateCell"
  ]

  connect() {
    this.sessions = JSON.parse(this.element.dataset.calendarModalSessionsValue || "[]")
    this.boundEscape = this.handleEscape.bind(this)
  }

  disconnect() {
    this.toggleScrollLock(false, true)
    document.removeEventListener("keydown", this.boundEscape)
  }

  open(event) {
    if (event) event.preventDefault()
    if (!this.modalTarget.classList.contains("hidden")) return
    
    this.modalTarget.classList.remove("hidden")
    this.toggleScrollLock(true)
    document.addEventListener("keydown", this.boundEscape)
  }

  openAtDate(event) {
    if (event.target.closest("button") || event.target.closest("form") || event.target.closest("a[data-location-trigger]")) return
    
    if (event) event.preventDefault()
    this.showDay(event)
  }

  close(event) {
    if (event) event.preventDefault()
    if (this.modalTarget.classList.contains("hidden")) return

    this.closeLocation()
    this.modalTarget.classList.add("hidden")
    this.toggleScrollLock(false)
    document.removeEventListener("keydown", this.boundEscape)
  }

  openLocation(event) {
    event.preventDefault()
    event.stopPropagation()

    const modal = this.locationModalEl
    if (!modal) return

    const btn = event.currentTarget
    const location = btn.dataset.location || ""
    const lat = parseFloat(btn.dataset.latitude)
    const lng = parseFloat(btn.dataset.longitude)
    const inPerson = btn.dataset.inPerson === "true"
    const hasCoords = inPerson && !isNaN(lat) && !isNaN(lng)

    const title = modal.querySelector("[data-calendar-modal-target='locationTitle']")
    const address = modal.querySelector("[data-calendar-modal-target='locationAddress']")
    const hint = modal.querySelector("[data-calendar-modal-target='locationHint']")
    const directions = modal.querySelector("[data-calendar-modal-target='locationDirections']")
    const mapWrap = modal.querySelector("[data-calendar-modal-target='locationMapWrap']")
    const mapFrame = modal.querySelector("[data-calendar-modal-target='locationMapFrame']")

    if (title) title.textContent = inPerson ? "Where you'll train" : "Online session"
    if (address) {
      address.textContent = location || (inPerson ? "Location details coming soon." : "This session runs online — your trainer will share join details after booking.")
    }
    if (hint) {
      hint.textContent = hasCoords
        ? "Exact address shown after booking. Map pin is approximate for your area."
        : (inPerson ? "Open directions in your maps app for turn-by-turn navigation." : "")
    }

    if (directions) {
      const query = hasCoords ? `${lat},${lng}` : encodeURIComponent(location)
      directions.href = `https://www.google.com/maps/search/?api=1&query=${query}`
      directions.classList.toggle("hidden", !inPerson || (!hasCoords && !location))
    }

    if (mapWrap && mapFrame) {
      if (hasCoords) {
        const pad = 0.012
        const bbox = `${lng - pad},${lat - pad},${lng + pad},${lat + pad}`
        mapFrame.src = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${lat}%2C${lng}`
        mapWrap.classList.remove("hidden")
      } else {
        mapFrame.removeAttribute("src")
        mapWrap.classList.add("hidden")
      }
    }

    modal.classList.remove("hidden")
    document.addEventListener("keydown", this.boundEscape)
  }

  closeLocation(event) {
    if (event) event.preventDefault()
    const modal = this.locationModalEl
    if (!modal || modal.classList.contains("hidden")) return

    modal.classList.add("hidden")
    const mapFrame = modal.querySelector("[data-calendar-modal-target='locationMapFrame']")
    if (mapFrame) mapFrame.removeAttribute("src")
  }

  get locationModalEl() {
    return this.element.querySelector("[data-calendar-modal-target='locationModal']")
  }

  handleEscape(event) {
    if (event.key !== "Escape") return

    const locationModal = this.locationModalEl
    if (locationModal && !locationModal.classList.contains("hidden")) {
      this.closeLocation()
      return
    }

    if (!this.modalTarget.classList.contains("hidden")) {
      this.close()
    }
  }

  toggleScrollLock(lock, force = false) {
    const body = document.body
    const html = document.documentElement
    let openModals = parseInt(body.dataset.openModals || "0")

    if (lock) {
      openModals++
      body.dataset.openModals = openModals
      
      if (openModals === 1) {
        this.scrollPos = window.pageYOffset
        body.style.position = "fixed"
        body.style.top = `-${this.scrollPos}px`
        body.style.width = "100%"
        body.classList.add("overflow-hidden")
        html.classList.add("overflow-hidden")
      }
    } else {
      openModals = force ? 0 : Math.max(0, openModals - 1)
      body.dataset.openModals = openModals
      
      if (openModals === 0) {
        const scrollY = body.style.top
        body.style.position = ""
        body.style.top = ""
        body.style.width = ""
        body.classList.remove("overflow-hidden")
        html.classList.remove("overflow-hidden")
        if (!force) {
          window.scrollTo(0, parseInt(scrollY || "0") * -1)
        }
      }
    }
  }

  showDay(event) {
    if (event) event.preventDefault()
    this.open()

    const clickedElement = event.currentTarget
    const date = clickedElement.dataset.date
    const buttonColor = this.element.dataset.calendarModalButtonColorValue || "blue"
    const highlightBg = `bg-${buttonColor}-600`

    this.dateCellTargets.forEach((cell) => {
      const cellClasses = Array.from(cell.classList)
      const currentBg = cellClasses.find((c) => c.startsWith("bg-") && c.endsWith("-600"))
      if (currentBg) cell.classList.remove(currentBg)
      
      cell.classList.remove("text-white", "shadow-md")
      const textDiv = cell.querySelector("div")
      if (textDiv) textDiv.classList.remove("!text-white")
    })

    const modalDateCell = this.dateCellTargets.find((cell) => {
      return cell.dataset.date === date && this.modalTarget.contains(cell)
    })
    const targetCell = modalDateCell || clickedElement

    if (targetCell) {
      targetCell.classList.add(highlightBg, "shadow-md")
      const textDiv = targetCell.querySelector("div")
      if (textDiv) textDiv.classList.add("!text-white")
      targetCell.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }

    this.selectedDateTarget.textContent = new Date(date).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long"
    })

    const daySessions = this.sessionsForDate(date)
    this.renderSessions(daySessions)
    
    if (window.innerWidth < 1024) {
      this.sessionsListTarget.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  sessionsForDate(date) {
    const sessions = this.sessions.filter((session) => {
      return (session.calendar_date || session.start_time.slice(0, 10)) === date
    })
    if (this.element.dataset.calendarModalCompactCardsValue !== "true") return sessions

    return sessions.filter((session) => {
      const startTime = Date.parse(session.start_time || "")
      return Number.isNaN(startTime) || startTime >= Date.now()
    })
  }

  renderSessions(sessions) {
    if (sessions.length === 0) {
      this.sessionsListTarget.innerHTML = `
        <div class="text-center py-12 px-4">
          <div class="w-14 h-14 mx-auto mb-4 rounded-2xl bg-white border border-gray-200 flex items-center justify-center text-gray-300">
            <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
          </div>
          <p class="text-gray-500 font-bold text-sm">No slots available for this day.</p>
          <p class="text-gray-400 text-xs mt-1">Try another date on the calendar.</p>
        </div>`
      return
    }

    const buttonColor = this.element.dataset.calendarModalButtonColorValue || "blue"
    const actionLabel = this.element.dataset.calendarModalActionLabelValue || "Reserve"
    const isOwner = this.element.dataset.calendarModalIsOwnerValue === "true"
    const accent = this.accentClasses(buttonColor)

    this.sessionsListTarget.innerHTML = sessions.map((s) => this.sessionCardHtml(s, accent, actionLabel, isOwner, buttonColor)).join("")
  }

  sessionCardHtml(s, accent, actionLabel, isOwner, buttonColor) {
    const isManagement = s.is_management === true
    const isFull = s.is_full
    const booked = s.booked_count ?? 0
    const capacity = s.capacity ?? 0
    const spotsLeft = Math.max(0, capacity - booked)
    const fillPct = capacity > 0 ? Math.min(100, Math.round((booked / capacity) * 100)) : 0
    const compactCards = this.element.dataset.calendarModalCompactCardsValue === "true"
    const calendarColor = s.calendar_color || (isFull ? "red" : "blue")
    const calendarColors = {
      red: {
        card: "bg-red-50 border-red-100/90",
        pill: "bg-red-100 text-red-700 border-red-200",
        bar: "bg-red-500"
      },
      green: {
        card: "bg-green-50 border-green-100/90",
        pill: "bg-green-100 text-green-700 border-green-200",
        bar: "bg-green-500"
      },
      blue: {
        card: "bg-blue-50 border-blue-100/90",
        pill: "bg-blue-100 text-blue-700 border-blue-200",
        bar: "bg-blue-500"
      }
    }
    const calendarStyle = calendarColors[calendarColor] || calendarColors.blue

    const statusLabel = isFull ? "Fully booked" : `${spotsLeft}/${capacity} spots left`
    const statusPillClass = calendarStyle.pill
      
    const customers = s.customers || s.attendees || []
    const attendeesHtml = !compactCards && customers.length > 0 ? `
      <div class="pt-4 border-t border-gray-100/80">
        <p class="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2.5">Attendees</p>
          <div class="flex flex-wrap gap-2">
          ${customers.map((a) => {
            const name = this.escapeHtml(a.name || "Unknown")
            const avatar = a.avatar || a.avatar_url
            const initial = this.escapeHtml(a.initial || (a.name ? a.name[0] : "?"))
              return `
              <div class="flex items-center gap-2 bg-white/90 px-2.5 py-1.5 rounded-xl border border-gray-100 shadow-sm" title="${name}">
                <div class="w-6 h-6 rounded-full overflow-hidden bg-blue-50 shrink-0 ring-2 ring-white">
                  ${avatar ? `<img src="${this.escapeHtml(avatar)}" alt="" class="w-full h-full object-cover">` : `<div class="w-full h-full flex items-center justify-center text-[8px] font-black text-blue-600 uppercase">${initial}</div>`}
                </div>
                <span class="text-[10px] font-bold text-gray-700">${name}</span>
              </div>`
          }).join("")}
        </div>
      </div>` : ""

    const locationHtml = this.locationHtmlFor(s, compactCards)

    const reviewActionHtml = s.review_path ? `
      <a href="${s.review_path}" class="cursor-pointer flex w-full md:w-auto items-center justify-center bg-amber-400 hover:bg-amber-500 text-white font-black px-5 py-3.5 md:py-2.5 rounded-xl text-[10px] uppercase tracking-widest shadow-md transition active:scale-95">
        Leave a Review
      </a>` : ""

    const sideActionHtml = isManagement ? `
      <div class="flex gap-2 shrink-0">
        <button type="button" onclick="document.getElementById('edit-capacity-${s.id}').classList.toggle('hidden')" class="cursor-pointer p-2.5 bg-white border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition shadow-sm" title="Edit capacity">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
        </button>
        <a href="${s.details_path}" class="cursor-pointer p-2.5 ${accent.iconBg} rounded-xl ${accent.iconText} hover:opacity-90 transition shadow-sm" title="${this.escapeHtml(s.details_label || "Details")}">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
        </a>
      </div>` : (s.booking_path && !isOwner && !isFull) ? `
      <form action="${s.booking_path}" method="post" data-turbo="false" class="shrink-0">
        <input type="hidden" name="authenticity_token" value="${this.getMetaValue("csrf-token")}">
        <button type="submit" class="cursor-pointer ${accent.btnBg} ${accent.btnHover} text-white font-black px-6 py-3 rounded-2xl shadow-lg shadow-blue-500/20 transition transform active:scale-95 text-sm whitespace-nowrap">
          ${actionLabel}
        </button>
      </form>` : (isFull && !isOwner ? `<span class="shrink-0 text-[10px] font-black text-red-500 uppercase tracking-widest px-3 py-2 bg-red-50 rounded-xl border border-red-100">Waitlist only</span>` : "")

    const desktopActionsHtml = [sideActionHtml, reviewActionHtml].filter(Boolean).length > 0 ? `
      <div class="hidden md:flex shrink-0 flex-col items-end gap-2">
        ${sideActionHtml}
        ${reviewActionHtml}
      </div>` : ""

    const editFormHtml = isManagement ? `
      <div id="edit-capacity-${s.id}" class="hidden mt-4 pt-4 border-t border-gray-100 bg-gray-50/80 p-4 rounded-2xl">
          <form action="${s.edit_path}" method="post" data-turbo="false">
            <input type="hidden" name="_method" value="patch">
            <input type="hidden" name="authenticity_token" value="${this.getMetaValue("csrf-token")}">
          <div class="space-y-3">
              <div>
              <label class="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Total capacity</label>
              <input type="number" name="service_session[capacity]" value="${capacity}" min="${booked}" class="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-900">
            </div>
            <button type="submit" class="w-full cursor-pointer bg-gray-900 text-white font-black py-3 rounded-xl text-[10px] uppercase tracking-widest hover:bg-gray-800 transition">Update capacity</button>
            </div>
          </form>
      </div>` : ""

    return `
      <article class="group relative overflow-hidden ${compactCards ? "rounded-2xl" : "rounded-[1.75rem]"} border ${calendarStyle.card} shadow-[0_4px_24px_-8px_rgba(15,23,42,0.12)]">
        <div class="${compactCards ? "p-4 space-y-3" : "p-5 md:p-6 space-y-4"}">
          <div class="flex justify-between items-start gap-3 md:gap-4">
            <div class="min-w-0 flex-1 w-full">
              <p class="${compactCards ? "text-lg md:text-xl" : "text-2xl md:text-[1.65rem]"} font-black text-gray-900 tracking-tight leading-none tabular-nums">${this.escapeHtml(s.time_range || "Time TBC")}</p>
              <p class="text-[10px] font-black text-gray-400 uppercase tracking-[0.15em] mt-2">${this.escapeHtml(s.title || "Session")}</p>
              ${s.price_label ? `<p class="mt-2 text-sm font-black text-blue-700">${this.escapeHtml(s.price_label)}</p>` : ""}
              <div class="mt-3 flex flex-wrap items-center gap-2">
                <span class="inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${statusPillClass}">${statusLabel}</span>
                ${!isFull && capacity > 0 ? `<span class="text-[10px] font-bold text-gray-400">${booked}/${capacity} booked</span>` : ""}
              </div>
              ${!isFull && capacity > 0 ? `
                <div class="mt-3 h-1.5 w-full max-w-[200px] rounded-full bg-gray-100 overflow-hidden">
                  <div class="h-full rounded-full ${calendarStyle.bar} transition-all duration-500" style="width: ${fillPct}%"></div>
                </div>` : ""}
              <div class="mt-3">${locationHtml}</div>
            </div>
            ${sideActionHtml ? `<div class="md:hidden shrink-0">${sideActionHtml}</div>` : ""}
            ${desktopActionsHtml}
                    </div>
            ${attendeesHtml}
            ${editFormHtml}
          ${reviewActionHtml ? `<div class="md:hidden pt-4 border-t border-gray-100">${reviewActionHtml}</div>` : ""}
        </div>
      </article>`
  }

  locationHtmlFor(s, iconOnly = false) {
    const isOnline = s.in_person === false
    const hasLocation = !!(s.location || s.location_short)

    if (isOnline) {
      return `
      <span class="inline-flex items-center gap-1.5 text-[10px] font-black text-violet-600 uppercase tracking-widest">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-50">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
        </span>
        Online
      </span>`
    }

    if (!hasLocation) return ""

    if (this.locationModalEl) {
      return `
      <button type="button"
              data-location-trigger
              data-action="click->calendar-modal#openLocation"
              data-location="${this.escapeHtml(s.location || s.location_short || "")}"
              data-latitude="${s.latitude ?? ""}"
              data-longitude="${s.longitude ?? ""}"
              data-in-person="true"
              aria-label="Show location"
              title="Show location"
              class="inline-flex items-center gap-1.5 text-[11px] font-black text-blue-600 hover:text-blue-800 uppercase tracking-widest transition group/loc cursor-pointer">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-600 group-hover/loc:bg-blue-100 transition">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
        </span>
        ${iconOnly ? "" : "Show location"}
      </button>`
    }

    return `<p class="text-[10px] font-bold text-gray-500 truncate max-w-[220px]">${this.escapeHtml(s.location_short || s.location)}</p>`
  }

  accentClasses(color) {
    const map = {
      blue: {
        cardBorder: "border-blue-100/80 hover:border-blue-200",
        topBar: "bg-gradient-to-r from-blue-500 to-indigo-400",
        pillBg: "bg-emerald-50",
        pillText: "text-emerald-700",
        pillBorder: "border-emerald-100",
        barFill: "bg-blue-500",
        btnBg: "bg-blue-600",
        btnHover: "hover:bg-blue-700",
        iconBg: "bg-blue-50",
        iconText: "text-blue-600"
      },
      green: {
        cardBorder: "border-green-100/80 hover:border-green-200",
        topBar: "bg-gradient-to-r from-green-500 to-emerald-400",
        pillBg: "bg-emerald-50",
        pillText: "text-emerald-700",
        pillBorder: "border-emerald-100",
        barFill: "bg-green-500",
        btnBg: "bg-green-600",
        btnHover: "hover:bg-green-700",
        iconBg: "bg-green-50",
        iconText: "text-green-600"
      }
    }
    return map[color] || map.blue
  }

  escapeHtml(str) {
    if (str == null) return ""
    const div = document.createElement("div")
    div.textContent = String(str)
    return div.innerHTML
  }

  getMetaValue(name) {
    const element = document.head.querySelector(`meta[name="${name}"]`)
    return element ? element.getAttribute("content") : ""
  }
}
